import React, { useState, useEffect, useCallback } from 'react';
import { paymentProfilesApi, PaymentProfile } from '../services/api';
import '../styles/reports.css';
import '../styles/regional-offices.css';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../i18n';

interface RegionalOffice {
  id: number;
  code: string;
  name: string;
  city: string | null;
  address: string | null;
  address_line2: string | null;
  phone: string | null;
  email: string | null;
  company_name: string | null;
  vat_id: string | null;
  vat_rate: number | null;
  tax_id_label: string | null;
  timezone: string;
  currency: string;
  document_language: string;
  status: string;
  is_hub: boolean;
  /** The office's single point of contact — not its only administrator. */
  manager: { id: number; email: string; name: string } | null;
  default_manager: { id: number; email: string; name: string } | null;
  /** Everyone running this office; several administrators are normal. */
  staff: { id: number; email: string; role: string; name: string | null }[];
  user_count: number;
  country_count: number;
  countries: { code: string; name: string }[];
  created_at: string | null;
  updated_at: string | null;
  payment_profile_id: number | null;
  payment_profile_name: string | null;
  cost_discount_percent: number;
  partner_id: string | null;
}

interface AdsPartner {
  partner_id: string;
  label: string;
  is_default: boolean;
}

interface Country {
  id: number;
  code: string;
  name: string;
  office: { id: number; code: string; name: string } | null;
}

interface Manager {
  id: number;
  email: string;
  name: string;
  role: string;
  regional_office_id: number | null;
}

const TIMEZONES = [
  'UTC',
  'Europe/Kyiv',
  'Europe/Warsaw',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Moscow',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Almaty',
  'Asia/Tashkent',
  'Asia/Tbilisi',
  'Asia/Tokyo',
  'Asia/Dubai',
];

const CURRENCIES = ['EUR', 'USD', 'AED', 'UAH', 'KZT', 'PLN', 'GBP'];

// `value` is the stored document_language — only labelKey is interface text.
const DOCUMENT_LANGUAGES = [
  { value: 'en', labelKey: 'offices.langEn' },
  { value: 'uk', labelKey: 'offices.langUk' },
  { value: 'ru', labelKey: 'offices.langRu' },
];

const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper to extract error message from API response
const extractErrorMessage = (data: any, fallback: string): string => {
  if (Array.isArray(data.detail)) {
    return data.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  return data.detail || fallback;
};

const RegionalOfficesPage: React.FC = () => {
  const { t } = useTranslation();
  const [offices, setOffices] = useState<RegionalOffice[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCountriesModal, setShowCountriesModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPartnerCostModal, setShowPartnerCostModal] = useState(false);
  const [showMarkupModal, setShowMarkupModal] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<RegionalOffice | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  const fetchOffices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);

      const response = await fetch(`${API_BASE}/api/admin/regional-offices?${params}`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setOffices(data.offices || []);
      } else {
        throw new Error('Failed to fetch offices');
      }
    } catch (err) {
      setError(t('offices.errLoad'));
      console.error(err);
    }
  }, [statusFilter, getAuthHeaders]);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/countries`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setCountries(data.countries || []);
      }
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  }, [getAuthHeaders]);

  const fetchManagers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/regional-offices/managers/candidates`, {
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        setManagers(data.managers || []);
      }
    } catch (err) {
      console.error('Failed to fetch managers:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchOffices(), fetchCountries(), fetchManagers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchOffices, fetchCountries, fetchManagers]);

  const filteredOffices = offices.filter(office => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (
        !office.code.toLowerCase().includes(query) &&
        !office.name.toLowerCase().includes(query) &&
        !(office.city || '').toLowerCase().includes(query)
      ) {
        return false;
      }
    }
    return true;
  });

  const handleDeactivate = async (officeId: number) => {
    if (!window.confirm(t('offices.confirmDeactivate'))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/regional-offices/${officeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (response.ok) {
        await fetchOffices();
      } else {
        const data = await response.json();
        setError(extractErrorMessage(data, t('offices.errDeactivate')));
      }
    } catch (err) {
      setError(t('offices.errDeactivate'));
    }
  };

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('offices.title')}</h1>
          <p className="header-subtitle">{t('offices.subtitle')}</p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="info-box" style={{ marginBottom: '24px', borderLeftColor: '#e74c3c', background: 'rgba(231, 76, 60, 0.1)' }}>
          <strong>{t('clients.errorLabel')}</strong> {error}
          <button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* Main Layout */}
      <div className="reports-layout">
        {/* Filters Panel */}
        <aside className="filters-panel">
          {/* Search */}
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('offices.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('offices.status')}</h3>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">{t('billing.allStatuses')}</option>
              <option value="active">{t('offices.stActive')}</option>
              <option value="inactive">{t('offices.stInactive')}</option>
            </select>
          </div>

          {/* Actions */}
          <div className="filter-section" style={{ marginTop: 'auto' }}>
            <button className="btn-primary" style={{ width: '100%' }} onClick={() => setShowCreateModal(true)}>
              <svg viewBox="0 0 24 24" fill="currentColor" style={{ width: 18, height: 18, marginRight: 8 }}>
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              {t('offices.createOffice')}
            </button>
          </div>
        </aside>

        {/* Results Area */}
        <div className="results-area visible">
          {loading ? (
            <div className="offices-loading">
              <div className="offices-spinner" />
            </div>
          ) : (
            <div className="offices-table-container">
              <table className="offices-table">
                <thead>
                  <tr>
                    <th>{t('offices.colOffice')}</th>
                    <th>{t('offices.colCountries')}</th>
                    <th>{t('offices.colManager')}</th>
                    <th>{t('offices.colDefaultManager')}</th>
                    <th>{t('offices.colUsers')}</th>
                    <th>{t('offices.status')}</th>
                    <th>{t('billing.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOffices.map(office => (
                    <tr key={office.id}>
                      <td>
                        <div className="offices-office-cell">
                          <div className={`offices-icon ${office.is_hub ? 'hub' : ''}`}>
                            {office.is_hub ? (
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                              </svg>
                            )}
                          </div>
                          <div className="offices-office-info">
                            <span className="offices-office-name">
                              {office.name}
                              {office.is_hub && <span className="offices-hub-badge">Hub</span>}
                            </span>
                            <span className="offices-office-meta">
                              {office.code} {office.city && `- ${office.city}`}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="offices-countries">
                          {office.countries.slice(0, 3).map(c => (
                            <span key={c.code} className="offices-country-chip">{c.code}</span>
                          ))}
                          {office.countries.length > 3 && (
                            <span className="offices-country-more">+{office.countries.length - 3}</span>
                          )}
                          {office.countries.length === 0 && office.is_hub && (
                            <span className="offices-countries-fallback">{t('offices.countriesDefault')}</span>
                          )}
                          {office.countries.length === 0 && !office.is_hub && (
                            <span className="offices-countries-none">{t('offices.countriesNone')}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {office.manager ? (
                          <span className="offices-manager">{office.manager.name}</span>
                        ) : (
                          <span className="offices-no-manager">{t('offices.notAssigned')}</span>
                        )}
                      </td>
                      <td>
                        {office.default_manager ? (
                          <span className="offices-manager">{office.default_manager.name}</span>
                        ) : (
                          <span className="offices-no-manager">{t('offices.notAssigned')}</span>
                        )}
                      </td>
                      <td>
                        <span className="offices-user-count">{office.user_count}</span>
                      </td>
                      <td>
                        <span className={`offices-status-badge ${office.status}`}>
                          {t(office.status === 'active' ? 'offices.stActive' : 'offices.stInactive')}
                        </span>
                      </td>
                      <td>
                        <div className="offices-actions">
                          <button
                            className="offices-action-btn"
                            title={t('offices.actEdit')}
                            onClick={() => {
                              setSelectedOffice(office);
                              setShowEditModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                          <button
                            className="offices-action-btn"
                            title={t('offices.actCountries')}
                            onClick={() => {
                              setSelectedOffice(office);
                              setShowCountriesModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10"/>
                              <line x1="2" y1="12" x2="22" y2="12"/>
                              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                            </svg>
                          </button>
                          <button
                            className="offices-action-btn"
                            title={t('offices.actPricing')}
                            onClick={() => {
                              setSelectedOffice(office);
                              setShowPricingModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 3h18v18H3z"/>
                              <path d="M3 9h18M3 15h18M9 3v18M15 3v18"/>
                            </svg>
                          </button>
                          <button
                            className="offices-action-btn"
                            title={t('offices.actPartnerCost')}
                            onClick={() => {
                              setSelectedOffice(office);
                              setShowPartnerCostModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="1" x2="12" y2="23"/>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                          </button>
                          <button
                            className="offices-action-btn"
                            title={t('offices.actMarkup')}
                            onClick={() => {
                              setSelectedOffice(office);
                              setShowMarkupModal(true);
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="19" y1="5" x2="5" y2="19"/>
                              <circle cx="6.5" cy="6.5" r="2.5"/>
                              <circle cx="17.5" cy="17.5" r="2.5"/>
                            </svg>
                          </button>
                          {!office.is_hub && office.status === 'active' && (
                            <button
                              className="offices-action-btn offices-action-deactivate"
                              title={t('offices.actDeactivate')}
                              onClick={() => handleDeactivate(office.id)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredOffices.length === 0 && (
                    <tr>
                      <td colSpan={7} className="offices-empty">
                        {t('offices.noneFound')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Create Office Modal */}
      {showCreateModal && (
        <CreateOfficeModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchOffices();
          }}
          managers={managers}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Edit Office Modal */}
      {showEditModal && selectedOffice && (
        <EditOfficeModal
          office={selectedOffice}
          onClose={() => {
            setShowEditModal(false);
            setSelectedOffice(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedOffice(null);
            fetchOffices();
          }}
          managers={managers}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Research Pricing Modal */}
      {showPricingModal && selectedOffice && (
        <ResearchPricingModal
          office={selectedOffice}
          onClose={() => {
            setShowPricingModal(false);
            setSelectedOffice(null);
          }}
          onSuccess={() => {
            setShowPricingModal(false);
            setSelectedOffice(null);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Partner Cost Modal */}
      {showPartnerCostModal && selectedOffice && (
        <PartnerCostModal
          office={selectedOffice}
          onClose={() => {
            setShowPartnerCostModal(false);
            setSelectedOffice(null);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Markup Stages Modal */}
      {showMarkupModal && selectedOffice && (
        <MarkupStagesModal
          office={selectedOffice}
          onClose={() => {
            setShowMarkupModal(false);
            setSelectedOffice(null);
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}

      {/* Manage Countries Modal */}
      {showCountriesModal && selectedOffice && (
        <ManageCountriesModal
          office={selectedOffice}
          countries={countries}
          onClose={() => {
            setShowCountriesModal(false);
            setSelectedOffice(null);
          }}
          onSuccess={() => {
            setShowCountriesModal(false);
            setSelectedOffice(null);
            fetchOffices();
            fetchCountries();
          }}
          getAuthHeaders={getAuthHeaders}
        />
      )}
    </>
  );
};

// Create Office Modal
interface CreateOfficeModalProps {
  onClose: () => void;
  onSuccess: () => void;
  managers: Manager[];
  getAuthHeaders: () => Record<string, string>;
}

const CreateOfficeModal: React.FC<CreateOfficeModalProps> = ({ onClose, onSuccess, managers, getAuthHeaders }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    city: '',
    address: '',
    address_line2: '',
    phone: '',
    email: '',
    timezone: 'UTC',
    currency: 'EUR',
    document_language: 'en',
    manager_user_id: 0,
    default_manager_user_id: 0,
    company_name: '',
    vat_id: '',
    vat_rate: '',
    tax_id_label: '',
    payment_profile_id: 0,
    cost_discount_percent: '0',
  });
  const [paymentProfiles, setPaymentProfiles] = useState<PaymentProfile[]>([]);
  useEffect(() => {
    paymentProfilesApi.list().then(setPaymentProfiles).catch(() => setPaymentProfiles([]));
  }, []);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const payload = {
        code: formData.code,
        name: formData.name,
        city: formData.city || null,
        address: formData.address || null,
        address_line2: formData.address_line2 || null,
        phone: formData.phone || null,
        email: formData.email || null,
        timezone: formData.timezone,
        currency: formData.currency,
        document_language: formData.document_language,
        manager_user_id: formData.manager_user_id > 0 ? formData.manager_user_id : null,
        default_manager_user_id: formData.default_manager_user_id > 0 ? formData.default_manager_user_id : null,
        company_name: formData.company_name || null,
        vat_id: formData.vat_id || null,
        vat_rate: formData.vat_rate === '' ? null : Number(String(formData.vat_rate).replace(',', '.')),
        tax_id_label: formData.tax_id_label || null,
        payment_profile_id: formData.payment_profile_id > 0 ? formData.payment_profile_id : null,
        cost_discount_percent: formData.cost_discount_percent === '' ? 0 : Number(String(formData.cost_discount_percent).replace(',', '.')),
      };

      const response = await fetch(`${API_BASE}/api/admin/regional-offices`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(extractErrorMessage(data, t('offices.errCreate')));
      }
    } catch (err) {
      setError(t('common.networkShort'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('offices.createTitle')}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="offices-modal-body">
            {error && <div className="offices-modal-error">{error}</div>}

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.code')}</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="UA-KYIV"
                  required
                  maxLength={20}
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Kyiv Office"
                  required
                />
              </div>
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.city')}</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Kyiv"
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.phone')}</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+380 44 123 4567"
                />
              </div>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.address1')}</label>
              <input
                type="text"
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
                placeholder={t('offices.address1Ph')}
              />
            </div>

            <div className="offices-form-field">
              <label>{t('offices.address2')}</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={e => setFormData({ ...formData, address_line2: e.target.value })}
                placeholder={t('offices.address2Ph')}
              />
            </div>

            <div className="offices-form-field">
              <label>{t('auth.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="office@example.com"
              />
            </div>

            <div className="offices-form-field">
              <label>{t('offices.legalName')}</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={e => setFormData({ ...formData, company_name: e.target.value })}
                placeholder={t('offices.legalNamePh')}
              />
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.taxIdLabel')}</label>
                <input
                  type="text"
                  value={formData.tax_id_label}
                  onChange={e => setFormData({ ...formData, tax_id_label: e.target.value })}
                  placeholder="VAT, BIN, TIN..."
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.taxIdNumber')}</label>
                <input
                  type="text"
                  value={formData.vat_id}
                  onChange={e => setFormData({ ...formData, vat_id: e.target.value })}
                  placeholder="IE1234567T"
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.vatRate')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.vat_rate}
                  onChange={e => setFormData({ ...formData, vat_rate: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.timezone')}</label>
                <select
                  value={formData.timezone}
                  onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div className="offices-form-field">
                <label>{t('offices.currency')}</label>
                <select
                  value={formData.currency}
                  onChange={e => setFormData({ ...formData, currency: e.target.value })}
                >
                  {CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="offices-form-field">
                <label>{t('offices.documentLanguage')}</label>
                <select
                  value={formData.document_language}
                  onChange={e => setFormData({ ...formData, document_language: e.target.value })}
                >
                  {DOCUMENT_LANGUAGES.map(l => (
                    <option key={l.value} value={l.value}>{t(l.labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.paymentProfile')}</label>
              <select
                value={(formData as any).payment_profile_id ?? 0}
                onChange={e => setFormData({ ...(formData as any), payment_profile_id: parseInt(e.target.value) })}
              >
                <option value={0}>{t('offices.noPaymentProfile')}</option>
                {paymentProfiles.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.currency})</option>
                ))}
              </select>
              {paymentProfiles.length === 0 && (
                <small style={{ color: '#6c757d', fontSize: 11 }}>
                  {t('offices.noProfilesYet')}
                </small>
              )}
            </div>

            <div className="offices-form-field">
              <label>{t('offices.partnerDiscount')}</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.cost_discount_percent}
                onChange={e => setFormData({ ...formData, cost_discount_percent: e.target.value })}
                placeholder="0"
              />
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.partnerDiscountHint')}
              </small>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.manager')}</label>
              <select
                value={formData.manager_user_id}
                onChange={e => setFormData({ ...formData, manager_user_id: parseInt(e.target.value) })}
              >
                <option value={0}>{t('offices.notAssigned')}</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
            </div>

          </div>
          <div className="offices-modal-footer">
            <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="offices-btn offices-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'offices.creating' : 'offices.createOffice')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Office Modal
interface EditOfficeModalProps {
  office: RegionalOffice;
  onClose: () => void;
  onSuccess: () => void;
  managers: Manager[];
  getAuthHeaders: () => Record<string, string>;
}

const EditOfficeModal: React.FC<EditOfficeModalProps> = ({ office, onClose, onSuccess, managers, getAuthHeaders }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    code: office.code,
    name: office.name,
    city: office.city || '',
    address: office.address || '',
    address_line2: office.address_line2 || '',
    phone: office.phone || '',
    email: office.email || '',
    timezone: office.timezone,
    currency: office.currency,
    document_language: office.document_language || 'en',
    manager_user_id: office.manager?.id || 0,
    default_manager_user_id: office.default_manager?.id || 0,
    status: office.status,
    company_name: office.company_name || '',
    vat_id: office.vat_id || '',
    vat_rate: office.vat_rate !== null && office.vat_rate !== undefined ? String(office.vat_rate) : '',
    tax_id_label: office.tax_id_label || '',
    payment_profile_id: office.payment_profile_id || 0,
    cost_discount_percent: office.cost_discount_percent !== null && office.cost_discount_percent !== undefined ? String(office.cost_discount_percent) : '0',
    partner_id: office.partner_id || '',  // '' = default ADS partner
    kbe: (office as any).kbe || '',
    knp: (office as any).knp || '',
    signatory_name: (office as any).signatory_name || '',
    kz_service_code: (office as any).kz_service_code || '',
    accounting_email: (office as any).accounting_email || '',
  });
  const [paymentProfiles, setPaymentProfiles] = useState<PaymentProfile[]>([]);
  useEffect(() => {
    // Best-effort: only admins with 2FA can list profiles. If it fails (no 2FA),
    // we silently leave the dropdown empty so non-2FA admins still see the form.
    paymentProfilesApi.list().then(setPaymentProfiles).catch(() => setPaymentProfiles([]));
  }, []);
  const [adsPartners, setAdsPartners] = useState<AdsPartner[]>([]);
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/regional-offices/ads-partners`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { partners: [] })
      .then(d => setAdsPartners(d.partners || []))
      .catch(() => setAdsPartners([]));
  }, []);
  const [error, setError] = useState('');
  const [stampPreview, setStampPreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);

  // Load existing images
  useEffect(() => {
    const headers = getAuthHeaders();
    fetch(`/api/admin/regional-offices/${office.id}/stamp`, { headers }).then(r => r.ok ? r.json() : null).then(d => { if (d) setStampPreview(d.image); }).catch(() => {});
    fetch(`/api/admin/regional-offices/${office.id}/signature`, { headers }).then(r => r.ok ? r.json() : null).then(d => { if (d) setSignaturePreview(d.image); }).catch(() => {});
  }, [office.id]);

  const handleImageUpload = async (type: 'stamp' | 'signature', file: File) => {
    const setter = type === 'stamp' ? setUploadingStamp : setUploadingSignature;
    setter(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`/api/admin/regional-offices/${office.id}/${type}`, {
        method: 'POST', headers: { 'Authorization': getAuthHeaders()['Authorization'] }, body: fd,
      });
      if (!resp.ok) throw new Error(t('offices.errUpload'));
      const imgResp = await fetch(`/api/admin/regional-offices/${office.id}/${type}`, { headers: getAuthHeaders() });
      if (imgResp.ok) { const d = await imgResp.json(); type === 'stamp' ? setStampPreview(d.image) : setSignaturePreview(d.image); }
    } catch (e: any) { setError(e.message || t('offices.errUpload')); }
    finally { setter(false); }
  };

  const handleImageDelete = async (type: 'stamp' | 'signature') => {
    await fetch(`/api/admin/regional-offices/${office.id}/${type}`, { method: 'DELETE', headers: getAuthHeaders() }).catch(() => {});
    type === 'stamp' ? setStampPreview(null) : setSignaturePreview(null);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const payload: any = {
        name: formData.name,
        city: formData.city || null,
        address: formData.address || null,
        address_line2: formData.address_line2 || null,
        phone: formData.phone || null,
        email: formData.email || null,
        timezone: formData.timezone,
        currency: formData.currency,
        document_language: formData.document_language,
        payment_profile_id: formData.payment_profile_id,  // 0 = clear
        manager_user_id: formData.manager_user_id,  // 0 = clear, >0 = set manager
        default_manager_user_id: formData.default_manager_user_id,  // 0 = clear, >0 = set
        company_name: formData.company_name || null,
        vat_id: formData.vat_id || null,
        vat_rate: formData.vat_rate === '' ? null : Number(String(formData.vat_rate).replace(',', '.')),
        tax_id_label: formData.tax_id_label || null,
        cost_discount_percent: formData.cost_discount_percent === '' ? 0 : Number(String(formData.cost_discount_percent).replace(',', '.')),
        partner_id: formData.partner_id,  // '' clears → default ADS partner
        kbe: formData.kbe || null,
        knp: formData.knp || null,
        signatory_name: formData.signatory_name || null,
        kz_service_code: formData.kz_service_code || null,
        accounting_email: formData.accounting_email || null,
      };

      // Don't send code/status for Hub
      if (!office.is_hub) {
        payload.code = formData.code;
        payload.status = formData.status;
      }

      const response = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(extractErrorMessage(data, t('offices.errUpdate')));
      }
    } catch (err) {
      setError(t('common.networkShort'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('offices.editTitle')}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="offices-modal-body">
            {error && <div className="offices-modal-error">{error}</div>}

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.code')}</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  required
                  maxLength={20}
                  disabled={office.is_hub}
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.name')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.city')}</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.phone')}</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.address1')}</label>
              <input
                type="text"
                value={formData.address}
                onChange={e => setFormData({ ...formData, address: e.target.value })}
              />
            </div>

            <div className="offices-form-field">
              <label>{t('offices.address2')}</label>
              <input
                type="text"
                value={formData.address_line2}
                onChange={e => setFormData({ ...formData, address_line2: e.target.value })}
              />
            </div>

            <div className="offices-form-field">
              <label>{t('auth.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div className="offices-form-field">
              <label>{t('offices.legalName')}</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={e => setFormData({ ...formData, company_name: e.target.value })}
              />
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.taxIdLabel')}</label>
                <input
                  type="text"
                  value={formData.tax_id_label}
                  onChange={e => setFormData({ ...formData, tax_id_label: e.target.value })}
                  placeholder="VAT, BIN, TIN..."
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.taxIdNumber')}</label>
                <input
                  type="text"
                  value={formData.vat_id}
                  onChange={e => setFormData({ ...formData, vat_id: e.target.value })}
                />
              </div>
              <div className="offices-form-field">
                <label>{t('offices.vatRate')}</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={formData.vat_rate}
                  onChange={e => setFormData({ ...formData, vat_rate: e.target.value })}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="offices-form-row">
              <div className="offices-form-field">
                <label>{t('offices.timezone')}</label>
                <select
                  value={formData.timezone}
                  onChange={e => setFormData({ ...formData, timezone: e.target.value })}
                >
                  {TIMEZONES.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div className="offices-form-field">
                <label>{t('offices.currency')}</label>
                <select
                  value={formData.currency}
                  onChange={e => setFormData({ ...formData, currency: e.target.value })}
                >
                  {CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="offices-form-field">
                <label>{t('offices.documentLanguage')}</label>
                <select
                  value={formData.document_language}
                  onChange={e => setFormData({ ...formData, document_language: e.target.value })}
                >
                  {DOCUMENT_LANGUAGES.map(l => (
                    <option key={l.value} value={l.value}>{t(l.labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.paymentProfile')}</label>
              <select
                value={(formData as any).payment_profile_id ?? 0}
                onChange={e => setFormData({ ...(formData as any), payment_profile_id: parseInt(e.target.value) })}
              >
                <option value={0}>{t('offices.noPaymentProfile')}</option>
                {paymentProfiles.filter(p => p.is_active).map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.currency})</option>
                ))}
              </select>
              {paymentProfiles.length === 0 && (
                <small style={{ color: '#6c757d', fontSize: 11 }}>
                  {t('offices.noProfilesYet')}
                </small>
              )}
            </div>

            <div className="offices-form-field">
              <label>{t('offices.adsPartner')}</label>
              <select
                value={formData.partner_id}
                onChange={e => setFormData({ ...formData, partner_id: e.target.value })}
              >
                {adsPartners.map(p => (
                  <option key={p.partner_id} value={p.is_default ? '' : p.partner_id}>
                    {p.label} ({p.partner_id}){p.is_default ? t('offices.adsPartnerDefault') : ''}
                  </option>
                ))}
              </select>
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.adsPartnerHint')}
              </small>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.accountingEmail')}</label>
              <input type="email" value={formData.accounting_email}
                     onChange={e => setFormData({ ...formData, accounting_email: e.target.value })}
                     placeholder="accounting@…" />
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.accountingEmailHint')}
              </small>
            </div>

            {formData.currency === 'KZT' && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #eee' }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('offices.kzInvoice')}</div>
                <div className="offices-form-field">
                  <label>{t('offices.kbe')}</label>
                  <input type="text" value={formData.kbe} onChange={e => setFormData({ ...formData, kbe: e.target.value })} placeholder="17" />
                </div>
                <div className="offices-form-field">
                  <label>{t('offices.knp')}</label>
                  <input type="text" value={formData.knp} onChange={e => setFormData({ ...formData, knp: e.target.value })} placeholder="858" />
                </div>
                <div className="offices-form-field">
                  <label>{t('offices.signatory')}</label>
                  <input type="text" value={formData.signatory_name} onChange={e => setFormData({ ...formData, signatory_name: e.target.value })} placeholder={t('offices.signatoryPh')} />
                </div>
                <div className="offices-form-field">
                  <label>{t('offices.serviceCode')}</label>
                  <input type="text" value={formData.kz_service_code} onChange={e => setFormData({ ...formData, kz_service_code: e.target.value })} placeholder="00000000454" />
                </div>
              </div>
            )}

            <div className="offices-form-field">
              <label>{t('offices.partnerDiscount')}</label>
              <input
                type="text"
                inputMode="decimal"
                value={formData.cost_discount_percent}
                onChange={e => setFormData({ ...formData, cost_discount_percent: e.target.value })}
                placeholder="0"
              />
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.partnerDiscountHint')}
              </small>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.primaryContact')}</label>
              <select
                value={formData.manager_user_id}
                onChange={e => setFormData({ ...formData, manager_user_id: parseInt(e.target.value) })}
              >
                <option value={0}>{t('offices.notAssigned')}</option>
                {managers.map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.primaryContactHint')}
              </small>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.staff')}</label>
              {(office.staff && office.staff.length > 0) ? (
                <ul className="offices-staff-list">
                  {office.staff.map(s => (
                    <li key={s.id} className="offices-staff-item">
                      <span>{s.name || s.email}</span>
                      <span className="offices-staff-role">
                        {t(s.role === 'regional_admin' ? 'offices.staffAdmin' : 'offices.staffManager')}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="offices-staff-empty">{t('offices.staffEmpty')}</div>
              )}
              <small style={{ color: '#6c757d', fontSize: 11 }}>
                {t('offices.staffHint')}
              </small>
            </div>

            <div className="offices-form-field">
              <label>{t('offices.defaultManager')}</label>
              <select
                value={formData.default_manager_user_id}
                onChange={e => setFormData({ ...formData, default_manager_user_id: parseInt(e.target.value) })}
              >
                <option value={0}>{t('offices.notAssigned')}</option>
                {managers.filter(m => m.regional_office_id === office.id).map(m => (
                  <option key={m.id} value={m.id}>{m.name} ({m.email})</option>
                ))}
              </select>
            </div>

            {!office.is_hub && (
              <div className="offices-form-field">
                <label>{t('offices.status')}</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">{t('offices.stActive')}</option>
                  <option value="inactive">{t('offices.stInactive')}</option>
                </select>
              </div>
            )}

            <div className="offices-info-box">
              <div className="offices-info-row">
                <span>{t('offices.infoUsers')}</span>
                <strong>{office.user_count}</strong>
              </div>
              <div className="offices-info-row">
                <span>{t('offices.infoCountries')}</span>
                <strong>{office.country_count}</strong>
              </div>
              <div className="offices-info-row">
                <span>{t('offices.infoCreated')}</span>
                <span>{office.created_at ? new Date(office.created_at).toLocaleDateString(currentLocale()) : '—'}</span>
              </div>
            </div>
          </div>
          {/* Stamp & Signature */}
          <div className="offices-modal-body" style={{ borderTop: '1px solid var(--border-color)', paddingTop: 16, marginTop: 8 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 12 }}>{t('offices.documents')}</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Stamp */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>{t('offices.stamp')}</label>
                {stampPreview ? (
                  <div style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, background: '#fafbfc', textAlign: 'center' }}>
                    <img src={stampPreview} alt={t('offices.stamp')} style={{ maxWidth: '100%', maxHeight: 80 }} />
                    <button type="button" onClick={() => handleImageDelete('stamp')} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, border: '2px dashed var(--border-color)', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {t(uploadingStamp ? 'offices.uploading' : 'offices.uploadPng')}
                    <input type="file" accept="image/png" hidden onChange={e => { if (e.target.files?.[0]) handleImageUpload('stamp', e.target.files[0]); }} />
                  </label>
                )}
              </div>
              {/* Signature */}
              <div>
                <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>{t('offices.signature')}</label>
                {signaturePreview ? (
                  <div style={{ position: 'relative', border: '1px solid var(--border-color)', borderRadius: 8, padding: 8, background: '#fafbfc', textAlign: 'center' }}>
                    <img src={signaturePreview} alt={t('offices.signature')} style={{ maxWidth: '100%', maxHeight: 80 }} />
                    <button type="button" onClick={() => handleImageDelete('signature')} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&times;</button>
                  </div>
                ) : (
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, border: '2px dashed var(--border-color)', borderRadius: 8, cursor: 'pointer', fontSize: 12, color: 'var(--text-tertiary)' }}>
                    {t(uploadingSignature ? 'offices.uploading' : 'offices.uploadPng')}
                    <input type="file" accept="image/png" hidden onChange={e => { if (e.target.files?.[0]) handleImageUpload('signature', e.target.files[0]); }} />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="offices-modal-footer">
            <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="offices-btn offices-btn-primary" disabled={isSubmitting}>
              {t(isSubmitting ? 'offices.saving' : 'offices.saveChanges')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Manage Countries Modal
interface ManageCountriesModalProps {
  office: RegionalOffice;
  countries: Country[];
  onClose: () => void;
  onSuccess: () => void;
  getAuthHeaders: () => Record<string, string>;
}

const ManageCountriesModal: React.FC<ManageCountriesModalProps> = ({
  office,
  countries,
  onClose,
  onSuccess,
  getAuthHeaders
}) => {
  const { t } = useTranslation();
  const [selectedCodes, setSelectedCodes] = useState<string[]>(
    office.countries.map(c => c.code)
  );
  const [search, setSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Filter countries that are either unassigned or belong to this office
  const availableCountries = countries.filter(c =>
    c.office === null || c.office.id === office.id
  );

  const filteredCountries = availableCountries.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const toggleCountry = (code: string) => {
    if (selectedCodes.includes(code)) {
      setSelectedCodes(selectedCodes.filter(c => c !== code));
    } else {
      setSelectedCodes([...selectedCodes, code]);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/countries`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ country_codes: selectedCodes }),
      });

      if (response.ok) {
        onSuccess();
      } else {
        const data = await response.json();
        setError(extractErrorMessage(data, t('offices.errCountries')));
      }
    } catch (err) {
      setError(t('common.networkShort'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal offices-modal-countries" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <h2>{t('offices.countriesTitle', { office: office.name })}</h2>
          <button className="offices-modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}

          <div className="offices-countries-picker">
            {/* Selected chips */}
            <div className="offices-selected-chips">
              {selectedCodes.length === 0 ? (
                <span className="offices-chips-empty">{t('offices.noCountriesSelected')}</span>
              ) : (
                selectedCodes.slice(0, 10).map(code => {
                  const country = countries.find(c => c.code === code);
                  return (
                    <span key={code} className="offices-chip">
                      {code}
                      <button
                        type="button"
                        className="offices-chip-remove"
                        onClick={() => toggleCountry(code)}
                      >
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  );
                })
              )}
              {selectedCodes.length > 10 && (
                <span className="offices-chips-more">{t('offices.moreCount', { count: selectedCodes.length - 10 })}</span>
              )}
            </div>

            {/* Search */}
            <div className="offices-countries-search">
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t('offices.searchCountries')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>

            {/* Count */}
            <div className="offices-countries-count">
              <strong>{selectedCodes.length}</strong> {t('offices.ofSelected', { total: availableCountries.length })}
            </div>

            {/* List */}
            <div className="offices-countries-list">
              {filteredCountries.map(country => (
                <div
                  key={country.code}
                  className={`offices-country-item ${selectedCodes.includes(country.code) ? 'selected' : ''}`}
                  onClick={() => toggleCountry(country.code)}
                >
                  <span className="offices-checkbox">
                    <svg viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="offices-country-code">{country.code}</span>
                  <span className="offices-country-name">{country.name}</span>
                </div>
              ))}
              {filteredCountries.length === 0 && (
                <div className="offices-countries-empty">{t('offices.noCountriesFound')}</div>
              )}
            </div>
          </div>
        </div>
        <div className="offices-modal-footer">
          <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="offices-btn offices-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {t(isSubmitting ? 'offices.saving' : 'offices.saveCountries')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Research Pricing Modal (4×10 matrix)
// ============================================

interface ResearchPricingModalProps {
  office: RegionalOffice;
  onClose: () => void;
  onSuccess: () => void;
  getAuthHeaders: () => Record<string, string>;
}

// 100/250/500 exist only for CONTINUOUS delivery; cells hold the PLAIN base price
// (the ×1.5 continuous and ×2 boost multipliers are applied at compute time, not here).
const RESPONDENT_COUNTS = [100, 250, 500, 1000, 2500, 5000, 10000];
const QUESTIONS_RANGE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const ResearchPricingModal: React.FC<ResearchPricingModalProps> = ({
  office,
  onClose,
  onSuccess,
  getAuthHeaders,
}) => {
  const { t } = useTranslation();
  // matrix[respondent_count][questions_count] -> price string
  const [grid, setGrid] = useState<Record<number, Record<number, string>>>({});
  const [currency, setCurrency] = useState<string>(office.currency || '');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/research-pricing`, {
          headers: getAuthHeaders(),
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          throw new Error(extractErrorMessage(data, t('offices.errLoadPricing')));
        }
        const data = await resp.json();
        if (cancelled) return;
        setCurrency(data.currency || '');
        const next: Record<number, Record<number, string>> = {};
        for (const row of data.matrix || []) {
          next[row.respondent_count] = {};
          for (const cell of row.cells || []) {
            next[row.respondent_count][cell.questions_count] =
              cell.price === null || cell.price === undefined ? '' : String(cell.price);
          }
        }
        setGrid(next);
      } catch (e: any) {
        if (!cancelled) setError(e.message || t('offices.errLoadPricing'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [office.id, getAuthHeaders]);

  const updateCell = (resp: number, q: number, value: string) => {
    setGrid(prev => ({
      ...prev,
      [resp]: { ...(prev[resp] || {}), [q]: value },
    }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const cells: Array<{ respondent_count: number; questions_count: number; price: number }> = [];
      for (const resp of RESPONDENT_COUNTS) {
        for (const q of QUESTIONS_RANGE) {
          const raw = (grid[resp]?.[q] ?? '').toString().trim().replace(',', '.');
          if (raw === '') continue;
          const num = Number(raw);
          if (!Number.isFinite(num) || num < 0) {
            throw new Error(t('offices.errInvalidCell', { resp, q }));
          }
          cells.push({ respondent_count: resp, questions_count: q, price: num });
        }
      }
      const resp = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/research-pricing`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ cells }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(extractErrorMessage(data, t('offices.errSavePricing')));
      }
      onSuccess();
    } catch (e: any) {
      setError(e.message || t('offices.errSavePricing'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div
        className="offices-modal offices-modal-pricing"
        onClick={e => e.stopPropagation()}
      >
        <div className="offices-modal-header">
          <div>
            <h2 style={{ margin: 0 }}>{t('offices.pricingTitle', { office: office.name })}</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary, #888)' }}>
              {t('offices.pricingHint', { currency: currency || '—' })}
            </div>
          </div>
          <button type="button" className="offices-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center' }}>{t('offices.loadingDots')}</div>
          ) : (
            <table className="research-pricing-grid">
              <thead>
                <tr>
                  <th>{t('offices.gridHeader')}</th>
                  {QUESTIONS_RANGE.map(q => (
                    <th key={q}>{q}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {RESPONDENT_COUNTS.map(resp => (
                  <tr key={resp}>
                    <th>
                      {resp.toLocaleString('en-US').replace(/,/g, ' ')}
                      {resp < 1000 && <span style={{ fontSize: 11, color: '#8E3A9A', marginLeft: 4 }}>{t('offices.continuous')}</span>}
                    </th>
                    {QUESTIONS_RANGE.map(q => (
                      <td key={q}>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={grid[resp]?.[q] ?? ''}
                          placeholder="—"
                          onChange={e => updateCell(resp, q, e.target.value)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="offices-modal-footer">
          <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="offices-btn offices-btn-primary"
            onClick={handleSubmit}
            disabled={submitting || loading}
          >
            {t(submitting ? 'offices.savingDots' : 'offices.savePricing')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Partner Cost Modal — "what this WL partner office owes us" for a period.
interface PartnerCostClient {
  advertiser_id: string;
  name: string;
  markup_percent: number;
  raw_cost: number;
  raw_wg: number;
  full_price: number;
  agency_commission: number;
  research_price: number;
  partner_owes: number;
}
interface PartnerCostData {
  office_id: number;
  office_name: string;
  currency: string;
  date_from: string;
  date_to: string;
  cost_discount_percent: number;
  raw_cost: number;
  raw_wg: number;
  full_price: number;
  agency_commission: number;
  research_total: number;
  partner_owes: number;
  clients: PartnerCostClient[];
}
interface PartnerCostModalProps {
  office: RegionalOffice;
  onClose: () => void;
  getAuthHeaders: () => Record<string, string>;
}

const PartnerCostModal: React.FC<PartnerCostModalProps> = ({ office, onClose, getAuthHeaders }) => {
  const { t } = useTranslation();
  const monthStart = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  })();
  const todayStr = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  const [dateFrom, setDateFrom] = useState(monthStart);
  const [dateTo, setDateTo] = useState(todayStr);
  const [data, setData] = useState<PartnerCostData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fmt = (n: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      const resp = await fetch(
        `${API_BASE}/api/admin/regional-offices/${office.id}/partner-cost?${params}`,
        { headers: getAuthHeaders() },
      );
      const d = await resp.json();
      if (!resp.ok) throw new Error(extractErrorMessage(d, t('offices.errLoadGeneric')));
      setData(d);
    } catch (e: any) {
      setError(e.message || t('offices.errLoadGeneric'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [office.id, dateFrom, dateTo, getAuthHeaders]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal offices-modal-pricing" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <div>
            <h2 style={{ margin: 0 }}>{t('offices.partnerCostTitle', { office: office.name })}</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary, #888)' }}>
              {t('offices.partnerCostHint', {
                pct: office.cost_discount_percent || 0, currency: office.currency,
              })}
            </div>
          </div>
          <button type="button" className="offices-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}

          <div className="offices-form-row" style={{ alignItems: 'flex-end' }}>
            <div className="offices-form-field">
              <label>{t('offices.dateFrom')}</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div className="offices-form-field">
              <label>{t('offices.dateTo')}</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
            </div>
            <div className="offices-form-field" style={{ flex: '0 0 auto' }}>
              <button type="button" className="offices-btn offices-btn-primary" onClick={load} disabled={loading}>
                {t(loading ? 'offices.loadingDots' : 'offices.apply')}
              </button>
            </div>
          </div>

          {loading && !data ? (
            <div style={{ padding: 24, textAlign: 'center' }}>{t('offices.loadingDots')}</div>
          ) : data ? (
            <>
              <div className="offices-info-box" style={{ marginTop: 8 }}>
                <div className="offices-info-row">
                  <span>{t('offices.rawCost')}</span>
                  <strong>{fmt(data.raw_cost)} {data.currency}</strong>
                </div>
                <div className="offices-info-row">
                  <span>{t('offices.colRawWg', 'Raw WG')}</span>
                  <strong>{fmt(data.raw_wg)} {data.currency}</strong>
                </div>
                <div className="offices-info-row">
                  <span>{t('offices.fullPrice')}</span>
                  <strong>{fmt(data.full_price)} {data.currency}</strong>
                </div>
                <div className="offices-info-row">
                  <span>{t('offices.colAgencyCommission', 'Agency commission')}</span>
                  <strong>{fmt(data.agency_commission)} {data.currency}</strong>
                </div>
                <div className="offices-info-row">
                  <span>{t('offices.researchPrice')}</span>
                  <strong>{fmt(data.research_total)} {data.currency}</strong>
                </div>
                <div className="offices-info-row">
                  <span>{t('offices.discountLabel')}</span>
                  <strong>{data.cost_discount_percent}%</strong>
                </div>
                <div className="offices-info-row" style={{ fontSize: 15 }}>
                  <span><strong>{t('offices.partnerOwes')}</strong></span>
                  <strong>{fmt(data.partner_owes)} {data.currency}</strong>
                </div>
              </div>

              <table className="research-pricing-grid" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{t('offices.colClient')}</th>
                    <th>{t('offices.colMarkup')}</th>
                    <th>{t('offices.colRawCost')}</th>
                    <th>{t('offices.colRawWg', 'Raw WG')}</th>
                    <th>{t('offices.colFullPrice')}</th>
                    <th>{t('offices.colAgencyCommission', 'Agency commission')}</th>
                    <th>{t('offices.colResearch')}</th>
                    <th>{t('offices.colOwes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clients.map(c => (
                    <tr key={c.advertiser_id}>
                      <th style={{ textAlign: 'left' }}>{c.name}</th>
                      <td style={{ textAlign: 'center' }}>{c.markup_percent}%</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.raw_cost)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.raw_wg)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.full_price)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.agency_commission)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.research_price)}</td>
                      <td style={{ textAlign: 'right' }}>{fmt(c.partner_owes)}</td>
                    </tr>
                  ))}
                  {data.clients.length === 0 && (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary, #888)' }}>
                        {t('offices.noSpendInPeriod')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : null}
        </div>

        <div className="offices-modal-footer">
          <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Markup Stages Modal — date-versioned base ("regional") markup for an office.
interface MarkupStage {
  id: number;
  markup: number;
  effective_from: string;
  is_active: boolean;
  is_future: boolean;
}
interface MarkupStagesData {
  default_markup: number;
  current_markup: number;
  current_is_default: boolean;
  stages: MarkupStage[];
}
interface MarkupStagesModalProps {
  office: RegionalOffice;
  onClose: () => void;
  getAuthHeaders: () => Record<string, string>;
}

const MarkupStagesModal: React.FC<MarkupStagesModalProps> = ({ office, onClose, getAuthHeaders }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<MarkupStagesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMarkup, setNewMarkup] = useState('');
  const [newDate, setNewDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/markup-stages`, {
        headers: getAuthHeaders(),
      });
      const d = await resp.json();
      if (!resp.ok) throw new Error(extractErrorMessage(d, t('offices.errLoadGeneric')));
      setData(d);
    } catch (e: any) {
      setError(e.message || t('offices.errLoadGeneric'));
    } finally {
      setLoading(false);
    }
  }, [office.id, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const addStage = async () => {
    setError('');
    const m = Number(String(newMarkup).replace(',', '.'));
    if (!Number.isFinite(m) || m < 0) { setError(t('offices.errValidMarkup')); return; }
    if (!newDate) { setError(t('offices.errPickDate')); return; }
    setSaving(true);
    try {
      const resp = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/markup-stages`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ markup: m, effective_from: newDate }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(extractErrorMessage(d, t('offices.errAddStage')));
      }
      setNewMarkup('');
      setNewDate('');
      await load();
    } catch (e: any) {
      setError(e.message || t('offices.errAddStage'));
    } finally {
      setSaving(false);
    }
  };

  const deleteStage = async (id: number) => {
    setError('');
    try {
      const resp = await fetch(`${API_BASE}/api/admin/regional-offices/${office.id}/markup-stages/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        throw new Error(extractErrorMessage(d, t('offices.errDeleteStage')));
      }
      await load();
    } catch (e: any) {
      setError(e.message || t('offices.errDeleteStage'));
    }
  };

  const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}.${m}.${y}`;
  };

  return (
    <div className="offices-modal-overlay" onClick={onClose}>
      <div className="offices-modal" onClick={e => e.stopPropagation()}>
        <div className="offices-modal-header">
          <div>
            <h2 style={{ margin: 0 }}>{t('offices.markupTitle', { office: office.name })}</h2>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text-secondary, #888)' }}>
              {t('offices.markupHint')}
            </div>
          </div>
          <button type="button" className="offices-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="offices-modal-body">
          {error && <div className="offices-modal-error">{error}</div>}
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>{t('offices.loadingDots')}</div>
          ) : data ? (
            <>
              <div className="offices-info-box" style={{ marginBottom: 12 }}>
                <div className="offices-info-row">
                  <span>{t('offices.markupNow')}</span>
                  <strong>{data.current_markup}%{data.current_is_default ? t('offices.markupDefaultSuffix') : ''}</strong>
                </div>
              </div>

              <div className="offices-form-row" style={{ alignItems: 'flex-end' }}>
                <div className="offices-form-field">
                  <label>{t('offices.markupPercent')}</label>
                  <input type="text" inputMode="decimal" value={newMarkup}
                    onChange={e => setNewMarkup(e.target.value)} placeholder="280" />
                </div>
                <div className="offices-form-field">
                  <label>{t('offices.effectiveFrom')}</label>
                  <input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
                </div>
                <div className="offices-form-field" style={{ flex: '0 0 auto' }}>
                  <button type="button" className="offices-btn offices-btn-primary" onClick={addStage} disabled={saving}>
                    {t(saving ? 'offices.savingDots' : 'offices.addStage')}
                  </button>
                </div>
              </div>

              <table className="research-pricing-grid" style={{ marginTop: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>{t('offices.effectiveFrom')}</th>
                    <th>{t('offices.colMarkup')}</th>
                    <th>{t('offices.status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.stages.map(s => (
                    <tr key={s.id}>
                      <th style={{ textAlign: 'left' }}>{fmtDate(s.effective_from)}</th>
                      <td style={{ textAlign: 'center' }}>{s.markup}%</td>
                      <td style={{ textAlign: 'center' }}>{t(s.is_future ? 'offices.stageFuture' : 'offices.stageActive')}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button type="button" className="offices-action-btn offices-action-deactivate"
                          title={t('offices.delete')} onClick={() => deleteStage(s.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                  {data.stages.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary, #888)' }}>
                        {t('offices.noStages', { pct: data.default_markup })}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : null}
        </div>

        <div className="offices-modal-footer">
          <button type="button" className="offices-btn offices-btn-secondary" onClick={onClose}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default RegionalOfficesPage;
